/**
 * RAG (Retrieval-Augmented Generation) API service
 */

import { apiClient } from './client';

// Access auth store via global to avoid circular dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAuthStore = () => (window as any).__NEOKANBAN_AUTH_STORE__;

// Types matching backend schemas

export interface RAGConfig {
  baseUrl: string;
  apiKey: string;
}

export interface RAGStatus {
  isConfigured: boolean;
  baseUrl: string | null;
  totalKnowledgeBases: number;
  totalDocuments: number;
}

export interface RAGSource {
  documentId: string;
  documentName: string;
  content: string;
  relevanceScore: number;
  pageNumber: number | null;
  taskId: string | null;
  boardId: string | null;
  boardName: string | null;
}

export interface RAGQueryRequest {
  query: string;
  boardIds?: string[];
  projectId?: string;
  topK?: number;
  includeSources?: boolean;
}

export interface RAGQueryResponse {
  answer: string;
  sources: RAGSource[];
  processingTimeMs: number;
  knowledgeBaseIds: string[];
}

export interface BoardRAGStatus {
  boardId: string;
  boardName: string;
  hasKnowledgeBase: boolean;
  knowledgeBaseId: string | null;
  documentCount: number;
}

export interface ProjectRAGStatus {
  projectId: string;
  projectName: string;
  boards: BoardRAGStatus[];
  totalDocuments: number;
}

export interface RAGSyncRequest {
  force?: boolean;
}

export interface RAGSyncResponse {
  syncedCount: number;
  skippedCount: number;
  failedCount: number;
  errors: string[];
}

// =============================================================================
// Meeting RAG Types
// =============================================================================

export interface RAGMeetingSource {
  documentId: string;
  documentName: string;
  content: string;
  relevanceScore: number;
  meetingId: string | null;
  meetingTitle: string | null;
  meetingDate: string | null;
  projectId: string | null;
  projectName: string | null;
}

export interface RAGMeetingQueryRequest {
  query: string;
  meetingIds?: string[];
  projectId?: string;
  topK?: number;
}

export interface RAGMeetingQueryResponse {
  answer: string;
  sources: RAGMeetingSource[];
  processingTimeMs: number;
  knowledgeBaseIds: string[];
}

export interface MeetingsRAGStatus {
  isConfigured: boolean;
  hasKnowledgeBase: boolean;
  knowledgeBaseId: string | null;
  documentCount: number;
  indexedMeetingCount: number;
}

export interface MeetingSyncResponse {
  synced: number;
  skipped: number;
  failed: number;
}

// Helper to get organization ID for query params
const getOrgId = (): string => {
  const { currentOrgId } = getAuthStore().getState();
  if (!currentOrgId) {
    throw new Error('No organization selected');
  }
  return currentOrgId;
};

export const ragApi = {
  /**
   * Get RAG integration status for current organization
   */
  getStatus: async (): Promise<RAGStatus> => {
    const response = await apiClient.get<RAGStatus>('/rag/status', {
      params: { organization_id: getOrgId() },
    });
    return response.data;
  },

  /**
   * Configure RAG integration for current organization
   */
  configure: async (config: RAGConfig): Promise<void> => {
    await apiClient.post(
      '/rag/configure',
      {
        base_url: config.baseUrl,
        api_key: config.apiKey,
      },
      {
        params: { organization_id: getOrgId() },
      }
    );
  },

  /**
   * Disconnect RAG integration
   */
  disconnect: async (): Promise<void> => {
    await apiClient.delete('/rag/disconnect', {
      params: { organization_id: getOrgId() },
    });
  },

  /**
   * Query RAG system (non-streaming)
   */
  query: async (request: RAGQueryRequest): Promise<RAGQueryResponse> => {
    const response = await apiClient.post<RAGQueryResponse>(
      '/rag/query',
      {
        query: request.query,
        board_ids: request.boardIds,
        project_id: request.projectId,
        top_k: request.topK ?? 5,
        include_sources: request.includeSources ?? true,
      },
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },

  /**
   * Stream query response from RAG system (Server-Sent Events)
   * Returns an EventSource for streaming responses
   */
  createQueryStream: (request: RAGQueryRequest): EventSource => {
    const config = window.__NEOKANBAN_CONFIG__ || {};
    const API_URL = config.API_URL ?? '';
    const { currentOrgId } = getAuthStore().getState();

    if (!currentOrgId) {
      throw new Error('No organization selected');
    }

    // Build query params
    const params = new URLSearchParams({
      organization_id: currentOrgId,
    });

    // For SSE, we need to use a POST request with fetch instead of EventSource
    // EventSource only supports GET, so we'll use a different approach
    // Create URL with org ID
    const url = `${API_URL}/api/v1/rag/query/stream?${params.toString()}`;

    // Note: EventSource doesn't support POST, so we need to create a custom implementation
    // For now, return a custom EventSource-like interface using fetch
    const eventSource = new EventSource(url);

    // Store request data for fetch-based streaming (see streamQuery below)
    (eventSource as EventSource & { _ragRequest: RAGQueryRequest })._ragRequest = request;

    return eventSource;
  },

  /**
   * Stream query using fetch API (supports POST with body)
   * Returns an async generator of response text chunks
   */
  streamQuery: async function* (
    request: RAGQueryRequest
  ): AsyncGenerator<string, void, unknown> {
    const config = window.__NEOKANBAN_CONFIG__ || {};
    const API_URL = config.API_URL ?? '';
    const { accessToken, currentOrgId } = getAuthStore().getState();

    if (!currentOrgId) {
      throw new Error('No organization selected');
    }

    const url = `${API_URL}/api/v1/rag/query/stream?organization_id=${currentOrgId}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-Organization-ID': currentOrgId,
      },
      body: JSON.stringify({
        query: request.query,
        board_ids: request.boardIds,
        project_id: request.projectId,
        top_k: request.topK ?? 5,
        include_sources: request.includeSources ?? true,
      }),
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({ message: 'Stream request failed' }))) as {
        message?: string;
      };
      throw new Error(error.message ?? `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              return;
            }
            // Parse the JSON chunk and extract only the text content
            try {
              const parsed = JSON.parse(data) as { chunk?: string; done?: boolean };
              if (parsed.chunk) {
                yield parsed.chunk;
              }
            } catch {
              // If not valid JSON, yield raw data (fallback)
              yield data;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Get RAG status for a specific board
   */
  getBoardStatus: async (boardId: string): Promise<BoardRAGStatus> => {
    const response = await apiClient.get<BoardRAGStatus>(`/rag/boards/${boardId}/status`, {
      params: { organization_id: getOrgId() },
    });
    return response.data;
  },

  /**
   * Sync all attachments from a board to RAG
   */
  syncBoard: async (boardId: string, force = false): Promise<RAGSyncResponse> => {
    const response = await apiClient.post<RAGSyncResponse>(
      `/rag/boards/${boardId}/sync`,
      { force },
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },

  /**
   * Get RAG status for a project and all its boards
   */
  getProjectStatus: async (projectId: string): Promise<ProjectRAGStatus> => {
    const response = await apiClient.get<ProjectRAGStatus>(`/rag/projects/${projectId}/status`, {
      params: { organization_id: getOrgId() },
    });
    return response.data;
  },

  // =============================================================================
  // Meeting RAG Methods
  // =============================================================================

  /**
   * Get RAG status for meetings in current organization
   */
  getMeetingsStatus: async (): Promise<MeetingsRAGStatus> => {
    const response = await apiClient.get<MeetingsRAGStatus>('/rag/meetings/status', {
      params: { organization_id: getOrgId() },
    });
    return response.data;
  },

  /**
   * Query meetings knowledge base (non-streaming)
   */
  queryMeetings: async (request: RAGMeetingQueryRequest): Promise<RAGMeetingQueryResponse> => {
    const response = await apiClient.post<RAGMeetingQueryResponse>(
      '/rag/meetings/query',
      {
        query: request.query,
        meeting_ids: request.meetingIds,
        project_id: request.projectId,
        top_k: request.topK ?? 5,
      },
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },

  /**
   * Stream query response from meetings knowledge base
   * Returns an async generator of response text chunks
   */
  streamQueryMeetings: async function* (
    request: RAGMeetingQueryRequest
  ): AsyncGenerator<string, void, unknown> {
    const config = window.__NEOKANBAN_CONFIG__ || {};
    const API_URL = config.API_URL ?? '';
    const { accessToken, currentOrgId } = getAuthStore().getState();

    if (!currentOrgId) {
      throw new Error('No organization selected');
    }

    const url = `${API_URL}/api/v1/rag/meetings/query/stream?organization_id=${currentOrgId}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-Organization-ID': currentOrgId,
      },
      body: JSON.stringify({
        query: request.query,
        meeting_ids: request.meetingIds,
        project_id: request.projectId,
        top_k: request.topK ?? 5,
      }),
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({ message: 'Stream request failed' }))) as {
        message?: string;
      };
      throw new Error(error.message ?? `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              return;
            }
            // Parse the JSON chunk and extract only the text content
            try {
              const parsed = JSON.parse(data) as { chunk?: string; done?: boolean };
              if (parsed.chunk) {
                yield parsed.chunk;
              }
            } catch {
              // If not valid JSON, yield raw data (fallback)
              yield data;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Sync all meeting notes to RAG
   */
  syncMeetings: async (force = false): Promise<MeetingSyncResponse> => {
    const response = await apiClient.post<MeetingSyncResponse>(
      '/rag/meetings/sync',
      {},
      {
        params: { organization_id: getOrgId(), force },
      }
    );
    return response.data;
  },
};
